import axios from "axios";

// const API_BASE_URL =
//   process.env.REACT_APP_API_URL || "http://localhost:5005/v1";

const API_BASE_URL = "https://api.hr-manage.net/v1";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token and partnership_id to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add partnership_id from selected company
  const selectedCompany = localStorage.getItem("selected_company");
  if (selectedCompany) {
    try {
      const company = JSON.parse(selectedCompany);
      // Add partnership_id as a query parameter for GET requests
      if (config.method === "get") {
        config.params = config.params || {};
        config.params.partnership_id = company.id;
      }
      // Add partnership_id to request body for POST/PATCH/PUT requests
      else if (["post", "patch", "put"].includes(config.method)) {
        // Handle FormData (for file uploads)
        if (config.data instanceof FormData) {
          config.data.append("partnership_id", company.id);
        }
        // Handle regular JSON objects
        else if (config.data && typeof config.data === "object") {
          config.data.partnership_id = company.id;
        }
      }
    } catch (error) {
      console.error("Failed to parse selected company:", error);
    }
  }

  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    // Handle 403 Forbidden / Access Denied
    if (error.response?.status === 403) {
      // Access denied - redirect to main page
      console.warn("Access denied, redirecting to main page");
      window.location.href = "/";
    }

    // Handle XML Access Denied responses (from S3 or similar services)
    if (error.response?.data && typeof error.response.data === "string") {
      if (
        error.response.data.includes("<Code>AccessDenied</Code>") ||
        error.response.data.includes("Access Denied")
      ) {
        console.warn(
          "Access denied detected in response, redirecting to main page",
        );
        window.location.href = "/";
      }
    }

    return Promise.reject(error);
  },
);

const apiService = {
  // Authentication
  login: async (username, password) => {
    const response = await api.post("/auth/login", { username, password });
    return response.data;
  },

  // Employees
  getEmployees: async (params = {}) => {
    const response = await api.get("/employees", { params });
    return response.data;
  },

  getEmployee: async (id, includeCustomer = true) => {
    const response = await api.get(`/employees/${id}`, {
      params: { include_customer: includeCustomer },
    });
    return response.data;
  },

  searchEmployees: async (name, includeCustomer = true) => {
    const response = await api.get("/employees/search", {
      params: { name, include_customer: includeCustomer },
    });
    return response.data;
  },

  createEmployee: async (data) => {
    const response = await api.post("/employees", data);
    return response.data;
  },

  updateEmployee: async (id, data) => {
    const response = await api.patch(`/employees/${id}`, data);
    return response.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  // Customers
  getCustomers: async (params = {}) => {
    const response = await api.get("/customers", { params });
    return response.data;
  },

  getCustomer: async (id, includeEmployees = false) => {
    const response = await api.get(`/customers/${id}`, {
      params: { include_employees: includeEmployees },
    });
    return response.data;
  },

  searchCustomers: async (params) => {
    const response = await api.get("/customers/search", { params });
    return response.data;
  },

  createCustomer: async (data) => {
    const response = await api.post("/customers", data);
    return response.data;
  },

  updateCustomer: async (id, data) => {
    const response = await api.patch(`/customers/${id}`, data);
    return response.data;
  },

  deleteCustomer: async (id) => {
    const response = await api.delete(`/customers/${id}`);
    return response.data;
  },

  deleteCustomerByName: async (customerName) => {
    const response = await api.delete("/customers/by-name", {
      data: { customer_name: customerName },
    });
    return response.data;
  },

  cleanupNoHoursCustomers: async () => {
    const response = await api.delete("/customers/cleanup-no-hours");
    return response.data;
  },

  // Salaries (old endpoints - kept for compatibility)
  generateSalaries: async (month) => {
    const response = await api.post("/salaries/generate", { month });
    return response.data;
  },

  // File Upload Preview (parse and show data before importing)
  previewUpload: async (file, dataType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data_type", dataType);

    const response = await api.post("/upload/preview", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // File Upload
  uploadFile: async (file, dataType, onProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("data_type", dataType);

    const response = await api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },

  clearUploadedData: async (month, dataType = "all") => {
    const selectedCompany = localStorage.getItem("selected_company");
    let partnershipId = 1;
    if (selectedCompany) {
      try {
        const company = JSON.parse(selectedCompany);
        partnershipId = company.id;
      } catch (error) {
        console.error("Failed to parse selected company:", error);
      }
    }
    const response = await api.delete("/upload/clear", {
      params: { month, partnership_id: partnershipId, data_type: dataType },
    });
    return response.data;
  },

  // Activities / Notifications
  getActivities: async (params = {}) => {
    const response = await api.get("/api/activities/", { params });
    return response.data;
  },

  markActivityRead: async (id) => {
    const response = await api.post(`/api/activities/${id}/mark-read`);
    return response.data;
  },

  dismissActivity: async (id) => {
    const response = await api.post(`/api/activities/${id}/dismiss`);
    return response.data;
  },

  markAllActivitiesRead: async () => {
    const response = await api.post("/api/activities/mark-all-read");
    return response.data;
  },

  getUnreadActivitiesCount: async () => {
    const response = await api.get("/api/activities/unread-count");
    return response.data;
  },

  // Employee Notes
  getEmployeeNotes: async (employeeId, includeAll = false) => {
    const response = await api.get(`/api/employee/${employeeId}/notes`, {
      params: { include_all: includeAll },
    });
    return response.data;
  },

  createEmployeeNote: async (employeeId, data) => {
    const response = await api.post(`/api/employee/${employeeId}/notes`, data);
    return response.data;
  },

  deleteNote: async (noteId) => {
    const response = await api.delete(`/api/notes/${noteId}`);
    return response.data;
  },

  updateNote: async (noteId, data) => {
    const response = await api.patch(`/api/notes/${noteId}`, data);
    return response.data;
  },

  // Employee Presence
  getPresence: async (month) => {
    const response = await api.get("/api/presence", {
      params: { month },
    });
    return response.data;
  },

  getPresenceAvailableMonths: async () => {
    const response = await api.get("/api/presence/months");
    return response.data;
  },

  getPresenceSummary: async (month) => {
    const response = await api.get(`/api/presence/summary/${month}`);
    return response.data;
  },

  createPresence: async (data) => {
    const response = await api.post("/api/presence", data);
    return response.data;
  },

  updatePresence: async (presenceId, data) => {
    const response = await api.put(`/api/presence/${presenceId}`, data);
    return response.data;
  },

  deletePresence: async (presenceId) => {
    const response = await api.delete(`/api/presence/${presenceId}`);
    return response.data;
  },

  getEmployeesWithoutPresence: async (month, customerId, partnershipId) => {
    const response = await api.get("/api/presence/employees-without-presence", {
      params: { month, customer_id: customerId, partnership_id: partnershipId },
    });
    return response.data;
  },

  // Employees Missing Rates
  getEmployeesMissingRates: async (partnershipId) => {
    const response = await api.get("/employees/missing-rates", {
      params: { partnership_id: partnershipId },
    });
    return response.data;
  },

  updateEmployeeRates: async (employeeId, data) => {
    const response = await api.put(`/employees/${employeeId}/rates`, data);
    return response.data;
  },

  // Employee-Customer (Multi-site) Management
  getEmployeeCustomers: async (employeeId) => {
    const response = await api.get(`/employees/${employeeId}/customers`);
    return response.data;
  },

  updateEmployeeCustomers: async (
    employeeId,
    customerIds,
    primaryCustomerId = null,
  ) => {
    const response = await api.put(`/employees/${employeeId}/customers`, {
      customer_ids: customerIds,
      primary_customer_id: primaryCustomerId,
    });
    return response.data;
  },

  addEmployeeCustomer: async (employeeId, customerId) => {
    const response = await api.post(
      `/employees/${employeeId}/customers/${customerId}`,
    );
    return response.data;
  },

  removeEmployeeCustomer: async (employeeId, customerId) => {
    const response = await api.delete(
      `/employees/${employeeId}/customers/${customerId}`,
    );
    return response.data;
  },

  // Salaries
  calculateSalaries: async (month, presenceMonth, password) => {
    const response = await api.post("/api/salaries/calculate", {
      month,
      presence_month: presenceMonth,
      password,
    });
    return response.data;
  },

  createSalary: async (salaryData) => {
    const response = await api.post("/api/salaries/", salaryData);
    return response.data;
  },

  getSalaries: async (month, partnership_id) => {
    const response = await api.get("/api/salaries/list", {
      params: { month, partnership_id },
    });
    return response.data;
  },

  updateSalary: async (salaryId, data) => {
    const response = await api.put(`/api/salaries/${salaryId}`, data);
    return response.data;
  },

  getSalarySummary: async (month) => {
    const response = await api.get("/api/salaries/summary", {
      params: { month },
    });
    return response.data;
  },

  // Salary Extras/Deductions
  getSalaryExtras: async (salaryId) => {
    const response = await api.get(`/api/salaries/${salaryId}/extras`);
    return response.data;
  },

  addSalaryExtra: async (salaryId, data) => {
    const response = await api.post(`/api/salaries/${salaryId}/extras`, data);
    return response.data;
  },

  updateSalaryExtra: async (salaryId, itemId, data) => {
    const response = await api.put(
      `/api/salaries/${salaryId}/extras/${itemId}`,
      data,
    );
    return response.data;
  },

  deleteSalaryExtra: async (salaryId, itemId) => {
    const response = await api.delete(
      `/api/salaries/${salaryId}/extras/${itemId}`,
    );
    return response.data;
  },

  // Company Salaries (חברה employees)
  getCompanySalaries: async (month, partnershipId = 1) => {
    const response = await api.get("/api/company-salaries/list", {
      params: { month, partnership_id: partnershipId },
    });
    return response.data;
  },

  updateCompanySalary: async (salaryId, data) => {
    const response = await api.put(`/api/company-salaries/${salaryId}`, data);
    return response.data;
  },

  getCompanySalarySummary: async (month, partnershipId = 1) => {
    const response = await api.get("/api/company-salaries/summary", {
      params: { month, partnership_id: partnershipId },
    });
    return response.data;
  },

  // Company Salary Extras/Deductions
  getCompanySalaryExtras: async (salaryId) => {
    const response = await api.get(`/api/company-salaries/${salaryId}/extras`);
    return response.data;
  },

  addCompanySalaryExtra: async (salaryId, data) => {
    const response = await api.post(
      `/api/company-salaries/${salaryId}/extras`,
      data,
    );
    return response.data;
  },

  updateCompanySalaryExtra: async (salaryId, itemId, data) => {
    const response = await api.put(
      `/api/company-salaries/${salaryId}/extras/${itemId}`,
      data,
    );
    return response.data;
  },

  deleteCompanySalaryExtra: async (salaryId, itemId) => {
    const response = await api.delete(
      `/api/company-salaries/${salaryId}/extras/${itemId}`,
    );
    return response.data;
  },

  calculateCompanySalaries: async (month, partnershipId) => {
    const response = await api.post("/api/company-salaries/calculate", {
      month,
      partnership_id: partnershipId,
    });
    return response.data;
  },

  // Export salaries to Excel (all salaries grouped by site)
  exportSalariesToExcel: async (month, partnershipId) => {
    const response = await api.get("/api/export/salaries/excel", {
      params: { month, partnership_id: partnershipId },
      responseType: "blob",
    });
    return response.data;
  },

  // Export Gold Clean T&M to Excel
  exportGoldTM: async (month, partnershipId) => {
    const response = await api.get("/api/report/export-gold-tm", {
      params: { month, partnership_id: partnershipId },
      responseType: "blob",
    });
    return response.data;
  },

  // User Management
  getUsers: async () => {
    const response = await api.get("/auth/users");
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post("/auth/signup", userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/auth/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.put(`/auth/users/${userId}`, userData);
    return response.data;
  },

  // User-Customer Assignment
  getUserCustomers: async (userId) => {
    const response = await api.get(`/api/users/${userId}/customers`);
    return response.data;
  },

  assignCustomersToUser: async (userId, customerIds) => {
    const response = await api.post(`/api/users/${userId}/customers`, {
      customer_ids: customerIds,
    });
    return response.data;
  },

  getMyCustomers: async () => {
    const response = await api.get("/api/my-customers");
    return response.data;
  },

  // Anomaly Detection
  getAnomalies: async (month = null, type = "all") => {
    const params = {};
    if (month) params.month = month;
    if (type) params.type = type;
    const response = await api.get("/api/anomalies", { params });
    return response.data;
  },

  getAnomaliesSummary: async (month = null) => {
    const params = {};
    if (month) params.month = month;
    const response = await api.get("/api/anomalies/summary", { params });
    return response.data;
  },

  // Enforcement
  getEnforcements: async (month = null, customerId = null) => {
    const params = {};
    if (month) params.month = month;
    if (customerId) params.customer_id = customerId;
    const response = await api.get("/api/enforcements", { params });
    return response.data;
  },

  createEnforcement: async (data) => {
    const response = await api.post("/api/enforcements", data);
    return response.data;
  },

  updateEnforcement: async (id, data) => {
    const response = await api.put(`/api/enforcements/${id}`, data);
    return response.data;
  },

  deleteEnforcement: async (id) => {
    const response = await api.delete(`/api/enforcements/${id}`);
    return response.data;
  },

  getEnforcementDashboard: async (month = null) => {
    const params = {};
    if (month) params.month = month;
    const response = await api.get("/api/enforcements/dashboard", { params });
    return response.data;
  },

  getEnforcementMonths: async () => {
    const response = await api.get("/api/enforcements/months");
    return response.data;
  },

  // Dashboard
  getDashboardStats: async (month = null) => {
    const params = month ? `?month=${month}` : "";
    const response = await api.get(`/api/dashboard/stats${params}`);
    return response.data;
  },

  getCustomerHours: async (month) => {
    const response = await api.get(
      `/api/dashboard/customer-hours?month=${month}`,
    );
    return response.data;
  },

  getCustomerSalaries: async (month) => {
    const response = await api.get(
      `/api/dashboard/customer-salaries?month=${month}`,
    );
    return response.data;
  },

  getAvailableMonths: async () => {
    const response = await api.get("/api/dashboard/available-months");
    return response.data;
  },

  // Partnerships
  getPartnerships: async () => {
    const response = await api.get("/api/partnerships/");
    return response.data;
  },

  getPartnership: async (id) => {
    const response = await api.get(`/api/partnerships/${id}`);
    return response.data;
  },

  createPartnership: async (data) => {
    const response = await api.post("/api/partnerships/", data);
    return response.data;
  },

  updatePartnership: async (id, data) => {
    const response = await api.patch(`/api/partnerships/${id}`, data);
    return response.data;
  },

  deletePartnership: async (id) => {
    const response = await api.delete(`/api/partnerships/${id}`);
    return response.data;
  },

  getPartnershipStats: async (id) => {
    const response = await api.get(`/api/partnerships/${id}/stats`);
    return response.data;
  },

  // New Employees (Recruiter submissions)
  getNewEmployees: async (status = "pending") => {
    const response = await api.get("/api/new-employees/", {
      params: { status },
    });
    return response.data;
  },

  createNewEmployee: async (data) => {
    const response = await api.post("/api/new-employees/create", data);
    return response.data;
  },

  approveNewEmployee: async (id) => {
    const response = await api.post(`/api/new-employees/${id}/approve`);
    return response.data;
  },

  rejectNewEmployee: async (id, reason) => {
    const response = await api.post(`/api/new-employees/${id}/reject`, {
      reason,
    });
    return response.data;
  },

  bulkApproveNewEmployees: async (ids) => {
    const response = await api.post("/api/new-employees/bulk-approve", { ids });
    return response.data;
  },

  getRecruiterStats: async () => {
    const response = await api.get("/api/new-employees/recruiter-stats");
    return response.data;
  },

  getApprovalLogs: async (limit = 100) => {
    const response = await api.get("/api/new-employees/approval-logs", {
      params: { limit },
    });
    return response.data;
  },

  getMyRecruiterStats: async () => {
    const response = await api.get("/api/new-employees/my-stats");
    return response.data;
  },

  changePassword: async (username, oldPassword, newPassword) => {
    const response = await api.post("/auth/change-password", {
      username,
      old_password: oldPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // Salary Pay (Payer)
  getSalariesToPay: async (month) => {
    const response = await api.get("/api/salaries-to-pay", {
      params: { month },
    });
    return response.data;
  },

  toggleSalaryPaid: async (salaryId) => {
    const response = await api.post(
      `/api/salaries-to-pay/${salaryId}/toggle-paid`,
    );
    return response.data;
  },

  bulkToggleSalaryPaid: async (salaryIds, setPaid) => {
    const response = await api.post("/api/salaries-to-pay/bulk-toggle", {
      salary_ids: salaryIds,
      set_paid: setPaid,
    });
    return response.data;
  },

  getSalaryPayMonths: async () => {
    const response = await api.get("/api/salaries-to-pay/months");
    return response.data;
  },

  // Profit Calculation
  getProfitCustomers: async () => {
    const response = await api.get("/api/profit/customers");
    return response.data;
  },

  calculateProfit: async (customerId, month, hours, hourRate) => {
    const response = await api.post("/api/profit/calculate", {
      customer_id: customerId,
      month,
      hours,
      hour_rate: hourRate,
    });
    return response.data;
  },

  getProfitSummary: async (month, customers) => {
    const response = await api.post("/api/profit/summary", {
      month,
      customers,
    });
    return response.data;
  },

  getCustomerCosts: async (customerId, month) => {
    const response = await api.get("/api/profit/customer-costs", {
      params: { customer_id: customerId, month },
    });
    return response.data;
  },

  getProfitData: async (month) => {
    const response = await api.get("/api/profit/data", {
      params: { month },
    });
    return response.data;
  },

  saveProfitData: async (month, customers) => {
    const response = await api.post("/api/profit/data", {
      month,
      customers,
    });
    return response.data;
  },

  getPresenceHours: async (month) => {
    const response = await api.get("/api/profit/presence-hours", {
      params: { month },
    });
    return response.data;
  },

  getEmployeeHours: async (month) => {
    const response = await api.get("/api/profit/employee-hours", {
      params: { month },
    });
    return response.data;
  },

  // Gold Clean Export
  exportGoldCleanReport: async (month) => {
    const response = await api.get("/api/gold-clean/export", {
      params: { month },
      responseType: "blob",
    });
    return response.data;
  },

  // Koral Export
  exportKoralReport: async (month) => {
    const response = await api.get("/api/koral/export", {
      params: { month },
      responseType: "blob",
    });
    return response.data;
  },

  // Koral Contractors
  getKoralContractors: async () => {
    const response = await api.get("/api/koral/contractors");
    return response.data;
  },

  // T&M Export
  exportTMReport: async (month, customerName = "", siteName = "") => {
    const params = { month };
    if (customerName) params.customer_name = customerName;
    if (siteName) params.site_name = siteName;
    const response = await api.get("/api/report/export-tm", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  // Companies Export
  exportCompaniesReport: async (month) => {
    const response = await api.get("/api/report/export-companies", {
      params: { month },
      responseType: "blob",
    });
    return response.data;
  },

  // היסטוריית משכורות - Employee Salary History
  getEmployeeSalaryHistory: async (employeeId, partnershipId = 1) => {
    const response = await api.get(
      `/api/salary-history/employee/${employeeId}`,
      {
        params: { partnership_id: partnershipId },
      },
    );
    return response.data;
  },

  // הלוואות - Loans
  getLoans: async (params = {}) => {
    const response = await api.get("/api/loans/list", { params });
    return response.data;
  },

  createLoan: async (data) => {
    const response = await api.post("/api/loans/", data);
    return response.data;
  },

  updateLoan: async (loanId, data) => {
    const response = await api.put(`/api/loans/${loanId}`, data);
    return response.data;
  },

  cancelLoan: async (loanId) => {
    const response = await api.delete(`/api/loans/${loanId}`);
    return response.data;
  },

  // Admin
  previewBulkRateUpdate: async (adminPassword, updates) => {
    const response = await api.post("/api/admin/bulk-update-rates-preview", {
      admin_password: adminPassword,
      updates,
    });
    return response.data;
  },

  bulkUpdateRates: async (adminPassword, updates) => {
    const response = await api.post("/api/admin/bulk-update-rates", {
      admin_password: adminPassword,
      updates,
    });
    return response.data;
  },

  // Onboarding / S3 files tracker (admin)
  getEmployeeOnboardingProfile: async (employeeId) => {
    const response = await api.get(
      `/api/onboarding/admin/employees/${employeeId}`,
    );
    return response.data;
  },

  getOnboardingEmployeesList: async (params = {}) => {
    const response = await api.get("/api/onboarding/admin/employees", {
      params,
    });
    return response.data;
  },

  uploadOnboardingIdImage: async (employeeId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const response = await api.post(
      `/api/onboarding/employees/${employeeId}/id-image`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  uploadOnboardingContract: async (employeeId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const response = await api.post(
      `/api/onboarding/employees/${employeeId}/contract`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  uploadOnboardingForm101: async (employeeId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const response = await api.post(
      `/api/onboarding/employees/${employeeId}/form101`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data;
  },

  deleteOnboardingIdImage: async (employeeId) => {
    const response = await api.delete(
      `/api/onboarding/employees/${employeeId}/id-image`,
    );
    return response.data;
  },

  deleteOnboardingContract: async (employeeId) => {
    const response = await api.delete(
      `/api/onboarding/employees/${employeeId}/contract`,
    );
    return response.data;
  },

  deleteOnboardingForm101: async (employeeId) => {
    const response = await api.delete(
      `/api/onboarding/employees/${employeeId}/form101`,
    );
    return response.data;
  },

  // Generic HTTP methods for custom endpoints
  get: async (url, config = {}) => {
    const response = await api.get(url, config);
    return response;
  },

  post: async (url, data = {}, config = {}) => {
    const response = await api.post(url, data, config);
    return response;
  },

  put: async (url, data = {}, config = {}) => {
    const response = await api.put(url, data, config);
    return response;
  },

  delete: async (url, config = {}) => {
    const response = await api.delete(url, config);
    return response;
  },
};

export default apiService;
